import { Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';
import { filter, flatMap, map, toArray } from 'rxjs/operators';
import { Aufgabe } from '../../domain/model/aufgabe';
import { VorgangsTyp } from '../../domain/model/vorgangsTyp';
import { AufgabeService } from '../../service/aufgabe/aufgabe.service';
import { AuthenticationService } from '../../service/auth/authentication.service';
import { UflaUser } from '../../service/auth/ufla-user';
import { ConfirmDialogService } from '../../service/common/confirm-dialog/confirm-dialog.service';
import { Organisation } from '../../service/organisation/organisation';
import { OrganisationService } from '../../service/organisation/organisation.service';
import { formatDate } from '@angular/common';
import { UpAndDownloadComponent } from '../../common/upload/up-and-download-component';
import { HttpClient } from '@angular/common/http';
import { OrganisationsTyp } from '../../domain/model/organisationsTyp';
import { NeueAufgabe } from '../../service/aufgabe/neue-aufgabe';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'ufla-erstelle-aufgabe-dialog',
    templateUrl: './erstelle-aufgabe-dialog.component.html',
    styleUrls: [
        './erstelle-aufgabe-dialog.component.scss',
    ],
    standalone: false,
})
export class ErstelleAufgabeDialogComponent extends UpAndDownloadComponent implements OnInit {
    readonly dialogRef = inject<MatDialogRef<ErstelleAufgabeDialogComponent>>(MatDialogRef);
    readonly authenticationService = inject(AuthenticationService);
    private readonly aufgabeService = inject(AufgabeService);
    private readonly organisationService = inject(OrganisationService);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly snackBar = inject(MatSnackBar);
    private readonly dialog = inject(MatDialog);
    private readonly formBuilder = inject(UntypedFormBuilder);
    protected http: HttpClient;
    data = inject<Aufgabe>(MAT_DIALOG_DATA);

    public errorMessage: string;
    public minFristDate = new Date();  // init minDate ensures selection in the past is prevented by matDatPicker
    public orgaTypeBasedGroupReceiverSelection = true;
    public singleReceiverSelectionOrganisations$: Observable<Organisation[]> = null;
    public uflaUser: UflaUser;
    public isUenb = false;
    public fileValidatorOptions = { maxFileSizeBytes: 150 * 1024 * 1024 };

    public readonly vorgangsTyps = [
        VorgangsTyp.AUFGABE,
        VorgangsTyp.TERMIN,
        VorgangsTyp.INFORMATION,
    ];

    public erstelleAufgabeForm = this.formBuilder.group({
        zuweisungen: this.formBuilder.group({
            assignToOrgTypUenb: [
                false,
            ],
            assignToOrgTypVnb: [
                false,
            ],
            assignToOrgTypnVnbNichtIntegriert: [
                false,
            ],
            assignToOrgTypnVnbTeilIntegriert: [
                false,
            ],
            assignToOrgTypnVnbVollIntegriert: [
                false,
            ],
        },
        {
            validators: [
                this.requireCheckboxesToBeCheckedValidator(1),
            ],
        }),
        kurzbeschreibung: [
            '',
        ],
        name: [
            '',
            Validators.required,
        ],
        type: [
            '',
            Validators.required,
        ],
        einzelEmpfaengerOrgId: [
            '',
        ],
        emailBenachrichtigungSenden: [
            false,
        ],
        emailBenachrichtigungNachVorgangabschluss: [
            false,
        ],
        erinnerungsMailSenden: [
            { value: false, disabled: true },
        ],
        file: [
            null,
        ],
        frist: [
            '',
            Validators.required,
        ],
        mitUenbTeilen: [
            false,
        ],
        orgaTypeLabel: [
            true,
            Validators.required,
        ],
    });

  @ViewChild('errorDialog', { static: false }) errorDialogTemplate: TemplateRef<any>;

  constructor() {
      super();

      this.authenticationService.user$
          .pipe(takeUntilDestroyed())
          .subscribe((user) => {
              this.uflaUser = user;
              if (this.uflaUser.organisationsTyp == OrganisationsTyp.UENB) {
                  this.vorgangsTyps.push(VorgangsTyp.GESAMTLAST);
              }
          });
  }

  public async ngOnInit() {
      this.singleReceiverSelectionOrganisations$ = this.organisationService.getOrganisationenWithZuweisungDeaktiviert(false)
          .pipe(
              flatMap(orgs => orgs),
              filter(org => org.id.toString() !== this.uflaUser.organisationsId),
              toArray<Organisation>(),
              map(org => org.sort((a, b) => a.name.localeCompare(b.name))),
          );

      // setzt den empfänger automatisch auf VNB wenn Nutzer ÜNB ist
      if (this.authenticationService.isUenb(this.authenticationService.user)) {
          this.isUenb = true;
          this.erstelleAufgabeForm.patchValue({ zuweisungen: { assignToOrgTypVnb: true } });
          this.erstelleAufgabeForm.controls.zuweisungen.clearValidators();
      }
  }

  public onFileUploadChanges(): void {
      this.erstelleAufgabeForm.controls.einzelEmpfaengerOrgId.reset();
  }

  public isFileError(): boolean {
      return this.erstelleAufgabeForm.controls.file.invalid;
  }

  public aufgabeOrgaTypeChanged(type: any): void {
      // @TODO Gruppe und Einzelempfaenger sollte nicht mit den Werten true/false unterschieden sein
      if (type === true && !this.isUenb) {
      // Gruppe
          this.orgaTypeBasedGroupReceiverSelection = true;
          this.erstelleAufgabeForm.controls.zuweisungen.setValidators([
              this.requireCheckboxesToBeCheckedValidator(1),
          ]);
          this.erstelleAufgabeForm.controls.zuweisungen.updateValueAndValidity();

          this.erstelleAufgabeForm.controls.einzelEmpfaengerOrgId.clearValidators();
          this.erstelleAufgabeForm.controls.einzelEmpfaengerOrgId.reset();
          this.erstelleAufgabeForm.controls.einzelEmpfaengerOrgId.updateValueAndValidity();
      } else {
      // Einzelempfaenger
          this.orgaTypeBasedGroupReceiverSelection = false;
          this.erstelleAufgabeForm.controls.einzelEmpfaengerOrgId.setValidators([
              Validators.required,
          ]);
          this.erstelleAufgabeForm.controls.einzelEmpfaengerOrgId.updateValueAndValidity();

          this.erstelleAufgabeForm.controls.zuweisungen.clearValidators();
          this.erstelleAufgabeForm.controls.zuweisungen.reset();
          this.erstelleAufgabeForm.controls.zuweisungen.updateValueAndValidity();
      }
  }

  public aufgabeTypeChanged(): void {

      // andere Aufgabentypen haben Gruppenempfänger
      this.erstelleAufgabeForm.controls.orgaTypeLabel.patchValue(true);

      if (!this.isUenb) {
      // zuweisungen nicht zurücksetzen, wenn Empfänger Westnetz (VNB) ist
          this.erstelleAufgabeForm.controls.zuweisungen.reset();
      }

      this.erstelleAufgabeForm.controls.kurzbeschreibung.reset();
      this.erstelleAufgabeForm.controls.name.reset();
      this.erstelleAufgabeForm.controls.einzelEmpfaengerOrgId.reset();
      this.erstelleAufgabeForm.controls.emailBenachrichtigungSenden.reset();
      this.erstelleAufgabeForm.controls.emailBenachrichtigungNachVorgangabschluss.reset();
      this.erstelleAufgabeForm.controls.erinnerungsMailSenden.reset();
      this.erstelleAufgabeForm.controls.frist.reset();
      this.erstelleAufgabeForm.controls.mitUenbTeilen.reset();

  }

  public prepareAufgabeForBackend(aufgabe): string {
      const zuweisungen = [];

      if (aufgabe.zuweisungen.value.assignToOrgTypUenb) {
          zuweisungen.push(OrganisationsTyp.UENB);
      }
      if (aufgabe.zuweisungen.value.assignToOrgTypVnb) {
          zuweisungen.push(OrganisationsTyp.VNB);
      }
      if (aufgabe.zuweisungen.value.assignToOrgTypnVnbNichtIntegriert) {
          zuweisungen.push(OrganisationsTyp.NVNB_NICHTINTEGRIERT);
      }
      if (aufgabe.zuweisungen.value.assignToOrgTypnVnbTeilIntegriert) {
          zuweisungen.push(OrganisationsTyp.NVNB_TEILINTEGRIERT);
      }
      if (aufgabe.zuweisungen.value.assignToOrgTypnVnbVollIntegriert) {
          zuweisungen.push(OrganisationsTyp.NVNB_VOLLINTEGRIERT);
      }


      const neueAufgabe = NeueAufgabe.createEmptyNeueAufgabe();
      neueAufgabe.name = aufgabe.name.value;
      neueAufgabe.frist = aufgabe.frist.value;
      neueAufgabe.mitUenbTeilen = !!aufgabe.mitUenbTeilen.value;
      neueAufgabe.emailBenachrichtigungSenden = !!aufgabe.emailBenachrichtigungSenden.value;
      neueAufgabe.emailBenachrichtigungNachVorgangabschluss = !!aufgabe.emailBenachrichtigungNachVorgangabschluss.value;
      neueAufgabe.erinnerungsMailSenden = !!aufgabe.erinnerungsMailSenden.value;
      //    hatDokument: aufgabe.file.value ? true : false,
      neueAufgabe.kurzbeschreibung = aufgabe.kurzbeschreibung.value;
      neueAufgabe.type = aufgabe.type.value;
      neueAufgabe.zuweisungen = zuweisungen;
      neueAufgabe.einzelEmpfaengerOrgId = aufgabe.einzelEmpfaengerOrgId.value;

      return JSON.stringify(neueAufgabe);
  }

  public async saveAufgabe() {
      if (await this.confirmDialogService.showDialog(
          'Wollen Sie diese Aufgabe speichern? Eine nachträgliche Änderung ist nicht mehr möglich.',
          'Speichern?',
          'primary',
          'Ja',
          'Nein')) {

          this.erstelleAufgabeForm.disable();
          let aufgabeCreationFailed = false;

          const stringifiedAufgabeJson = this.prepareAufgabeForBackend(this.erstelleAufgabeForm.controls);
          const parsedAufgabeToBeSent = JSON.parse(stringifiedAufgabeJson);

          try {
              const aufgabe = await this.aufgabeService.createAufgabe(parsedAufgabeToBeSent);
              const filesToUpload = this.erstelleAufgabeForm.controls.file.value;

              if (!aufgabe) {
                  aufgabeCreationFailed = true;
                  console.error('Anlegen des Vorgangs fehlgeschlagen.');
              }

              // on success and if a file was provided by the user, upload the file
              if (!aufgabeCreationFailed && filesToUpload && filesToUpload.length > 0) {
                  this.numberOfFilesToUpload = filesToUpload.length;
                  for (const file of filesToUpload) {
                      const attached = await this.aufgabeService.attachDocument(aufgabe, file, this);
                      if (!attached) {
                          aufgabeCreationFailed = true; // if the creation fails, don't send mails
                          console.error(`dokument ${file.name} NICHT angelegt..`);
                          break;
                      } else {
                          this.numberOfUploadedFiles++;
                      }
                  }
              }

              // send reminder and notifications if desired and all previous calls were successful.
              if (!aufgabeCreationFailed && (aufgabe.emailBenachrichtigungSenden || aufgabe.erinnerungsMail)) {
                  const mailsSend = await this.aufgabeService.sendMailsForAufgaben(aufgabe.id);
                  if (!mailsSend) {
                      aufgabeCreationFailed = true;
                      console.error('Fehler beim versenden der E-Mails zum Vorgang.');
                  }
              }

              if (aufgabeCreationFailed) {
                  this.dialog.open(this.errorDialogTemplate);
                  await this.aufgabeService.deleteAufgabe(aufgabe.id.toString(), false);
              } else {
                  this.snackBar.open('Aufgabe erfolgreich im Jahr '
            + formatDate(this.erstelleAufgabeForm.controls.frist.value, 'yyyy', 'DE') + ' angelegt.', 'Ok', {
                      duration: 5000,
                  });
              }

              this.dialogRef.close('created');
          } catch (error) {
              console.error(error);
              this.errorMessage = error.message;
              this.dialog.open(this.errorDialogTemplate);
              this.erstelleAufgabeForm.enable();
              this.dialogRef.close('error');
          }

      }
  }

  private requireCheckboxesToBeCheckedValidator(minRequired = 1): ValidatorFn {
      return function validate(formGroup: UntypedFormGroup) {
          let checked = 0;

          Object.keys(formGroup.controls).forEach(key => {
              const control = formGroup.controls[key];

              if (control.value === true) {
                  checked++;
              }
          });

          if (checked < minRequired) {
              return {
                  requireCheckboxesToBeChecked: true,
              };
          }

          return null;
      };
  }

  toggledEmailBenachrichtigungSenden($event: MatSlideToggleChange) {
      if (!$event.checked && this.erstelleAufgabeForm.controls['erinnerungsMailSenden'].value) {
          this.erstelleAufgabeForm.controls['erinnerungsMailSenden'].patchValue(false);
      }
      if ($event.checked) {
          this.erstelleAufgabeForm.controls['erinnerungsMailSenden'].enable();
      } else {
          this.erstelleAufgabeForm.controls['erinnerungsMailSenden'].disable();
      }
  }
}
